import {
	IColumnHeader,
	IPage,
	IPageRange,
	ISelectOption,
	ISelectProps,
	Loading,
	PagingOptions,
	SearchBox,
	Select,
} from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { IFilterExpression, IQueryDataField, IQueryOptions, ISearchExpression } from '../../../models/GroupAccessOwners/ServiceInterfaces';
import { FlatMap, Group, IsDefined } from '../../../utilities/GroupAccessOwners/CommonUtil';
import { Container } from './Container';
import { ITableViewProps, TableView } from './TableView';

export interface IQueryableTableViewProps<TRowData> extends Omit<ITableViewProps<TRowData>, 'rowData'> {
	filterables: IFilterable[];

	rowData: IPage<TRowData> | 'Loading';
	requestData(query: IQueryOptions): void;

	renderView(withComponents: IQueryableViewComponents): JSX.Element;
}

export interface IQueryableTableColumn extends IColumnHeader, IQueryDataField {
	isQueryable: true;
}

function IsQueryableTableColumn(column: IColumnHeader): column is IQueryableTableColumn {
	return IsDefined(column) && (column as IQueryableTableColumn).isQueryable === true;
}

export interface ISelectableTableColumn extends IColumnHeader {
	isSelectable: true;
	lockSelection?: true;
	makeDefaultSelectable?: true;
}

function IsSelectableTableColumn(column: IColumnHeader): column is ISelectableTableColumn {
	return IsDefined(column) && (column as ISelectableTableColumn).isSelectable === true;
}

export interface IFilterable {
	filterOnField: IQueryDataField & { displayName: string };
	for: { filterByDefault?: true; displayName: string; value: any }[];
}

interface IQueryableViewComponents {
	columnSelector: JSX.Element;

	filterSelector: JSX.Element;
	customFilterToggle: JSX.Element;

	pagination: JSX.Element;

	searchBar: JSX.Element;

	tableView: JSX.Element;
}

export class QueryableTableView<TRowData> extends React.Component<IQueryableTableViewProps<TRowData>> {
	// -- INITIALIZATION ROUTINES -- //

	// INTERNAL STATE MEMBERS
	private columnSelection: string[];

	private query: Readonly<{
		pagingOptions: Readonly<Pick<IPageRange, 'offset' | 'limit'>>;
		searchQuery: string;
		filterBy: IFilterExpression[];
	}>;

	public constructor(props: IQueryableTableViewProps<TRowData>) {
		super(props);

		// Initialize column selection
		this.columnSelection = this.defaultColumnSelection;

		// Initialize query parameters
		this.query = {
			pagingOptions: this.defaultPagination,
			searchQuery: '',
			filterBy: this.defaultFilters,
		};
	}

	// DEFAULTS
	private get defaultColumnSelection(): string[] {
		const { columns } = this.props;

		return columns
			.filter(IsSelectableTableColumn)
			.filter((c) => c.makeDefaultSelectable === true)
			.map((c) => c.field);
	}

	private get defaultFilters(): IFilterExpression[] {
		const { filterables } = this.props;

		return FlatMap(filterables, (f) => {
			return f.for
				.filter((x) => x.filterByDefault === true)
				.map((filterItem) => ({
					boundToEntity: f.filterOnField.boundToEntity,
					fieldName: f.filterOnField.fieldName,
					queryOperation: 'FilterBy',
					values: [filterItem.value],
				}));
		});
	}

	private get defaultPagination(): Readonly<Pick<IPageRange, 'offset' | 'limit'>> {
		return {
			offset: 0,
			limit: 10,
		};
	}

	private get ColumnsForTableView() {
		return this.props.columns.filter((col) => {
			// If the column is selectable
			if (IsSelectableTableColumn(col)) {
				// Check whether or not it has been selected
				if (this.columnSelection.some((s) => col.field === s)) {
					// If so, include it
					return true;
				}
			} else {
				// Include all fixed columns
				return true;
			}
		});
	}

	//// COMPONENT MOUNTING
	public componentDidMount() {
		// Perform an initial data request to populate the component
		this.PerformDataRequest();
	}

	//// COMPONENT RENDERING
	public render() {
		const { renderView } = this.props;

		// == Render the view == //
		return renderView(this.QueryableComponents());
	}

	private PerformDataRequest() {
		const { filterBy, pagingOptions, searchQuery } = this.query;

		// Construct search expressions
		const searchExpressions: ISearchExpression[] = searchQuery
			? this.ColumnsForTableView.filter(IsQueryableTableColumn).map((col) => ({
					boundToEntity: col.boundToEntity,
					fieldName: col.fieldName,
					queryOperation: 'SearchFor',
					values: [searchQuery],
			  }))
			: [];

		// Assemble compiled query
		const compiledQuery: IQueryOptions = {
			page: pagingOptions,
			queryExpressions: [...filterBy, ...searchExpressions],
		};

		// Invoke callback to request data
		this.props.requestData(compiledQuery);
	}

	/////// == QUERYABLE COMPONENTS ///////

	private QueryableComponents = (): IQueryableViewComponents => ({
		columnSelector: this.RenderColumnSelector(),

		filterSelector: this.RenderFilterSelector(),
		customFilterToggle: <></>,

		pagination: this.RenderPagination(),

		searchBar: this.RenderSearchBar(),

		tableView: this.RenderTableView(),
	});

	// -- PAGINATION -- //
	private RenderPagination() {
		const { rowData } = this.props;

		// If no people have been loaded, do not display the paginator
		if (rowData === 'Loading') {
			return <></>;
		}

		return <PagingOptions pageRange={rowData.pageRange} updatePageRange={this.UpdatePageRange} />;
	}

	private UpdatePageRange = (pageRange: IPageRange) => {
		// Update paging options
		this.query = {
			...this.query,

			pagingOptions: {
				...pageRange,
			},
		};

		// Perform a data request w/ the updated query options
		this.PerformDataRequest();
	};

	// -- TOOL MENU -- //

	// SEARCH
	private RenderSearchBar() {
		const { rowData } = this.props;

		return (
			<SearchBox
				callbackFunc={this.ExecuteSearchQuery}
				resultsCount={rowData !== 'Loading' ? rowData.pageRange.total : undefined}
				inputSize={'sm'}
				disabled={rowData === 'Loading'}
			/>
		);
	}

	private ExecuteSearchQuery = (searchText: string) => {
		this.query = {
			...this.query,

			// Update search query text
			searchQuery: searchText,

			// Reset page position
			pagingOptions: this.defaultPagination,
		};

		// Perform a data request
		this.PerformDataRequest();
	};

	private RenderColumnSelector() {
		const { columns } = this.props;

		const baseSelectorProps: ISelectProps = {
			displayText: 'Columns',
			multiple: true,
			optionsContainerWidth: '250px',
			selectWidth: '180px',
			showCount: true,
			size: 'sm',
		};

		// Obtain the columns that are selectable
		const selectableColumns = columns.filter(IsSelectableTableColumn);

		// Map selectable columns to options
		const selectableOptions: ISelectOption[] = selectableColumns.map((c) => ({
			displayText: c.label,
			name: c.field,

			disabled: c.lockSelection,
		}));

		return (
			<Select
				{...baseSelectorProps}
				items={selectableOptions}
				selectedValues={this.columnSelection}
				onApply={(sel) => this.UpdateColumnSelection(Array.from(sel ?? []))}
			/>
		);
	}

	private UpdateColumnSelection = (selection: string[]) => {
		this.columnSelection = selection;

		this.query = {
			...this.query,

			// Reset page position
			pagingOptions: this.defaultPagination,
		};

		// Perform a data request
		this.PerformDataRequest();
	};

	private RenderFilterSelector() {
		const { filterables } = this.props;

		const baseSelectorProps: ISelectProps = {
			displayText: 'Filter',
			multiple: true,
			optionsContainerWidth: '250px',
			selectWidth: '180px',
			showCount: true,
			size: 'sm',
		};

		// Flatly project filterables to a set of filter options/expressions
		type selectOptionWithDataField = ISelectOption & IQueryDataField & { value: string };
		const filters: selectOptionWithDataField[] = FlatMap(filterables, (f) => {
			return f.for.map((filterItem) => ({
				group: f.filterOnField.displayName,
				displayText: filterItem.displayName,
				name: `${f.filterOnField.boundToEntity}_${f.filterOnField.fieldName}_${filterItem.value}`,

				boundToEntity: f.filterOnField.boundToEntity,
				fieldName: f.filterOnField.fieldName,

				value: filterItem.value,
			}));
		});

		// Derive the current filter selection
		const currentFilterSelection = FlatMap(this.query.filterBy, (f) => {
			return f.values.map((v) => `${f.boundToEntity}_${f.fieldName}_${v}`);
		});

		const onApply = (values: string[]) => {
			const selected = filters.filter((f) => values.indexOf(f.name) > -1);

			const belongsToGroup = (option: selectOptionWithDataField, group: IQueryDataField) => {
				return option.boundToEntity === group.boundToEntity && option.fieldName === group.fieldName;
			};

			const expressions: IFilterExpression[] = Group(selected, belongsToGroup).map((g) => ({
				boundToEntity: g.group.boundToEntity,
				fieldName: g.group.fieldName,
				queryOperation: 'FilterBy',
				values: g.groupMembers.map((m) => m.value),
			}));

			// Invoke callback
			this.UpdateFilterExpressions(expressions);
		};

		return (
			<Select
				{...baseSelectorProps}
				items={filters}
				selectedValues={currentFilterSelection}
				onApply={(sel) => onApply(Array.from(sel || []))}
			/>
		);
	}

	private UpdateFilterExpressions = (filteringBy: IFilterExpression[]) => {
		this.query = {
			...this.query,

			// Update filter expressions
			filterBy: filteringBy,

			// Reset page position
			pagingOptions: this.defaultPagination,
		};

		// Perform a data request
		this.PerformDataRequest();
	};

	// VIEW
	private RenderTableView() {
		const { rowData } = this.props;

		// If the columns or table data is not available, display a loading indicator
		if (rowData === 'Loading') {
			return (
				<Container aligned={'center'}>
					<Loading height={'70px'} width={'70px'} />
				</Container>
			);
		}

		return <TableView {...this.props} columns={this.ColumnsForTableView} rowData={rowData.items} />;
	}
}
